import React, {
  createContext,
  useReducer,
  PropsWithChildren,
  useState,
  Dispatch,
} from "react";
import { IListContext } from "@common/components/ListDetailViews/ListView/ListContext";
import { TicketEntity } from "../ListView/colDefs";
import { Ticket } from "@services/tickets/types";
import { usePreservedListPage } from "@common/hooks/usePreservedListPage";
import { isPageAction } from "@common/utils/isPageAction";
import { isSortAction } from "@common/utils/isSortAction";
import { useSorting } from "@common/hooks/useSorting";

type TicketActions =
  | "reset"
  | "closeTicket"
  | "openNewTicket"
  | "updateTicket"
  | Partial<TicketContext>;

interface TicketContext extends IListContext<TicketEntity, TicketActions> {
  ticketId?: TicketEntity["id"];
  isNewTicket?: boolean;
}

const initialState = {
  dispatch: () => {},
  isNewTicket: true,
  page: 1,
  sort: { column: "id", direction: "desc" },
} as const;

export const TicketListContext = createContext<TicketContext>(initialState);

const reducer = (state: TicketContext, action: TicketActions) => {
  if (action === "reset")
    return {
      dispatch: state.dispatch,
      sort: state.sort,
      ticketId: state.ticketId,
    };
  if (action === "closeTicket")
    return {
      ...state,
      ticketId: undefined,
      isNewTicket: false,
    };
  if (action === "openNewTicket") return { ...state, isNewTicket: true };
  if (action === "updateTicket") {
    return {
      dispatch: state.dispatch,
      sort: state.sort,
      ticketId: state.ticketId,
      page: state.page,
    };
  }

  return { ...state, ...action };
};

export interface SelectedTicket {
  id: Required<Ticket>["id"];
  accountId: Required<Required<Ticket>["account"]>["id"];
}

export const SelectedTicketsContext = createContext<{
  selected: SelectedTicket[];
  setSelected: (selected: SelectedTicket[]) => void;
}>({ selected: [], setSelected: () => {} });

export const TicketProvider = (props: PropsWithChildren<{}>) => {
  const [selected, setSelected] = useState<SelectedTicket[]>([]);
  const [preservedPage, setPreservedPage] = usePreservedListPage();
  const [preservedSort, setPreservedSort] = useSorting<TicketEntity>();
  const [state, dispatch] = useReducer(reducer, {
    dispatch: () => {},
    page: preservedPage ?? initialState.page,
    sort: preservedSort ?? initialState.sort,
  });

  const preservedPageAndSortingMiddleware = (action: TicketActions) => {
    if (action === "reset") setPreservedPage(initialState.page);

    if (isPageAction(action)) setPreservedPage(action.page);

    if (isSortAction<TicketEntity>(action)) setPreservedSort(action.sort);
  };

  const dispatchWithMiddleware: Dispatch<TicketActions> = (action) => {
    dispatch(action);
    preservedPageAndSortingMiddleware(action);
  };

  return (
    <TicketListContext.Provider
      value={{ ...state, dispatch: dispatchWithMiddleware }}
    >
      <SelectedTicketsContext.Provider value={{ selected, setSelected }}>
        {props.children}
      </SelectedTicketsContext.Provider>
    </TicketListContext.Provider>
  );
};
